import React, {useState} from "react";
import {
    Box, Typography, Paper, CircularProgress, Divider, Alert,
    Table, TableBody, TableCell, TableContainer, TableRow,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {accountingApi} from "../../services/api/accounting";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import ReportFilters from "./ReportFilters";

const today = new Date();
const firstOfYear = `${today.getFullYear()}-01-01`;
const lastOfYear = `${today.getFullYear()}-12-31`;

const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return "KES 0.00";
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return num < 0 ? `(KES ${formatted})` : `KES ${formatted}`;
};

export default function IncomeStatement() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleApply = async (params) => {
        setLoading(true);
        try {
            const response = await accountingApi.getIncomeStatement(params);
            if (response.ok && response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (err) {
            console.error("Failed to fetch income statement:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePDF = async (params) => {
        setDownloading(true);
        try {
            const response = await accountingApi.downloadIncomeStatementPDF(params);
            if (response.ok && response.blob) {
                const url = window.URL.createObjectURL(response.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `KRA_Income_Statement_${params.start_date}_to_${params.end_date}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            } else {
                showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                Income Statement (KRA Tax Report)
            </Typography>
            <ReportFilters onApply={handleApply} onDownloadPDF={handlePDF} downloading={downloading}
                defaultStart={firstOfYear} defaultEnd={lastOfYear}/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
            ) : !data ? (
                <Paper sx={{py: 6, textAlign: "center", borderRadius: 2}}>
                    <Typography color="textSecondary">Select a date range and click Apply to generate the report.</Typography>
                </Paper>
            ) : (
                <Box>
                    <Alert severity="info" icon={<InfoIcon/>} sx={{mb: 2}}>
                        This is an estimated tax calculation. Consult your tax advisor for official KRA filing.
                    </Alert>

                    <Paper sx={{borderRadius: 2, overflow: "hidden"}}>
                        {data.period && (
                            <Box sx={{px: 3, py: 1.5, bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0"}}>
                                <Typography variant="body2" color="textSecondary">
                                    Period: {data.period.start} to {data.period.end}
                                </Typography>
                            </Box>
                        )}

                        {/* Gross Rental Income */}
                        <Box sx={{px: 3, py: 2}}>
                            <Box sx={{display: "flex", justifyContent: "space-between", mb: 2}}>
                                <Typography variant="subtitle1" fontWeight={600}>Gross Rental Income</Typography>
                                <Typography variant="subtitle1" fontWeight={700} sx={{color: "#2e7d32"}}>{formatMoney(data.gross_rental_income)}</Typography>
                            </Box>

                            {/* Other Income */}
                            {data.other_income?.length > 0 && (
                                <>
                                    <Typography variant="body2" fontWeight={600} sx={{mb: 1, color: "#666"}}>Other Income</Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableBody>
                                                {data.other_income.map((item, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={{border: "none", pl: 2, py: 0.5}}>{item.code} - {item.name}</TableCell>
                                                        <TableCell align="right" sx={{border: "none", py: 0.5}}>{formatMoney(item.amount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Box sx={{display: "flex", justifyContent: "space-between", mt: 1, px: 2}}>
                                        <Typography variant="body2" fontWeight={500} color="textSecondary">Total Other Income</Typography>
                                        <Typography variant="body2" fontWeight={500}>{formatMoney(data.total_other_income)}</Typography>
                                    </Box>
                                </>
                            )}
                        </Box>

                        <Divider/>

                        {/* Total Income */}
                        <Box sx={{px: 3, py: 1.5, bgcolor: "#e8f5e9"}}>
                            <Box sx={{display: "flex", justifyContent: "space-between"}}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{color: "#2e7d32"}}>Total Income</Typography>
                                <Typography variant="subtitle1" fontWeight={700} sx={{color: "#2e7d32"}}>{formatMoney(data.total_income)}</Typography>
                            </Box>
                        </Box>

                        <Divider/>

                        {/* Deductions */}
                        <Box sx={{px: 3, py: 2}}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1, color: "#e65100"}}>Deductions</Typography>
                            {data.deductions?.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableBody>
                                            {data.deductions.map((item, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={{border: "none", pl: 2}}>{item.code} - {item.name}</TableCell>
                                                    <TableCell align="right" sx={{border: "none", fontWeight: 500}}>{formatMoney(item.amount)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell sx={{fontWeight: 700, borderTop: "2px solid #e65100", color: "#e65100"}}>Total Deductions</TableCell>
                                                <TableCell align="right" sx={{fontWeight: 700, borderTop: "2px solid #e65100", color: "#e65100"}}>{formatMoney(data.total_deductions)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : <Typography variant="body2" color="textSecondary" sx={{pl: 2}}>No deductions in this period.</Typography>}
                        </Box>

                        <Divider/>

                        {/* Tax Calculation */}
                        <Box sx={{px: 3, py: 2, bgcolor: "#fff8e1"}}>
                            <Box sx={{display: "flex", justifyContent: "space-between", mb: 1}}>
                                <Typography variant="subtitle1" fontWeight={600}>Taxable Income</Typography>
                                <Typography variant="subtitle1" fontWeight={700}>{formatMoney(data.taxable_income)}</Typography>
                            </Box>
                            <Box sx={{display: "flex", justifyContent: "space-between", mb: 0.5}}>
                                <Typography variant="body2" color="textSecondary">Tax Rate (KRA Residential Rental)</Typography>
                                <Typography variant="body2" fontWeight={500}>{data.tax_rate}%</Typography>
                            </Box>
                            <Box sx={{display: "flex", justifyContent: "space-between", mb: 1}}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{color: "#c62828"}}>Tax Payable</Typography>
                                <Typography variant="subtitle1" fontWeight={700} sx={{color: "#c62828"}}>{formatMoney(data.tax_payable)}</Typography>
                            </Box>
                        </Box>

                        <Divider/>

                        {/* Net Income After Tax */}
                        <Box sx={{px: 3, py: 2, bgcolor: data.net_income_after_tax >= 0 ? "#e8f5e9" : "#ffebee"}}>
                            <Box sx={{display: "flex", justifyContent: "space-between"}}>
                                <Typography variant="h6" fontWeight={700}>Net Income After Tax</Typography>
                                <Typography variant="h6" fontWeight={700} sx={{color: data.net_income_after_tax >= 0 ? "#2e7d32" : "#c62828"}}>
                                    {formatMoney(data.net_income_after_tax)}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            )}
        </Box>
    );
}
